// Service Worker-registrering for PWA + offline.
// Eksponerer en enkel API slik at appen kan vise en "ny versjon"-toast
// og selv styre når den nye service workeren tar over.

let refreshing = false;

// Hvor ofte vi spør serveren om det finnes en ny versjon (mens appen er åpen).
const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000; // 30 min

// Sett til true for å aktivere ny versjon AUTOMATISK (uten "Oppdater"-knapp).
// Advarsel: appen lastes på nytt umiddelbart når en ny versjon er klar, så
// brukeren kan miste data de holder på å fylle inn (f.eks. en fangst).
const AUTO_APPLY_UPDATES = false;

/**
 * Registrer service worker.
 * @param {Object} callbacks
 * @param {(registration: ServiceWorkerRegistration) => void} [callbacks.onUpdate]
 *   Kalles når en ny versjon er installert og venter på å bli aktivert.
 * @param {(registration: ServiceWorkerRegistration) => void} [callbacks.onSuccess]
 *   Kalles når service worker er registrert (første gang / aktiv).
 */
export function register(callbacks = {}) {
  if (!('serviceWorker' in navigator)) return;

  // I utvikling registrerer vi IKKE service worker. Den cacher /static/-chunks
  // (stale-while-revalidate) og app-shell-HTML, mens CRA-dev-serveren bygger
  // chunks på nytt hele tiden. Resultatet blir at SW serverer en utdatert
  // index.html / chunk som peker på filer webpack allerede har byttet ut –
  // og lazy import() feiler med "ChunkLoadError".
  // Vi fjerner samtidig en eventuell SW som ble registrert i en tidligere økt.
  if (process.env.NODE_ENV !== 'production') {
    unregister();
    return;
  }

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        // Hjelper: håndter en ventende/ny versjon likt uansett hvordan den ble oppdaget.
        const handleWaiting = () => {
          if (AUTO_APPLY_UPDATES) {
            activateUpdate(registration); // skipWaiting → controllerchange → reload
          } else {
            callbacks.onUpdate?.(registration);
          }
        };

        // En oppdatering som allerede venter (f.eks. ved ny faneåpning)
        if (registration.waiting && navigator.serviceWorker.controller) {
          handleWaiting();
        }

        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed') {
              if (navigator.serviceWorker.controller) {
                // Ny versjon klar, men en gammel kjører fortsatt
                handleWaiting();
              } else {
                // Første installasjon → ferdig cachet for offline
                callbacks.onSuccess?.(registration);
              }
            }
          });
        });

        // Sjekk etter ny versjon med jevne mellomrom mens appen er åpen,
        // og hver gang appen får fokus igjen (typisk når en PWA på hjemskjerm
        // åpnes på nytt). Dette hindrer at brukeren blir sittende fast på en
        // gammel versjon fordi nettleseren ellers sjekker sjelden.
        const checkForUpdate = () => {
          registration.update().catch(() => {
            /* offline e.l. – prøver igjen senere */
          });
        };

        setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);

        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') checkForUpdate();
        });

        window.addEventListener('focus', checkForUpdate);
      })
      .catch((error) => {
        console.log('SW registrering feilet:', error);
      });

    // Når den nye service workeren tar over, last siden på nytt én gang
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  });
}

/**
 * Be den ventende service workeren om å aktivere seg umiddelbart.
 * Trigger 'controllerchange' → reload.
 */
export function activateUpdate(registration) {
  const waiting = registration?.waiting;
  if (waiting) {
    waiting.postMessage({ type: 'SKIP_WAITING' });
  }
}

/**
 * Avregistrer alle service workers og tøm cachene de har lagt opp.
 * Brukes i utvikling for å fjerne en SW som ble registrert i en tidligere
 * produksjons-/dev-økt og som ellers serverer utdaterte chunks.
 */
export function unregister() {
  if (!('serviceWorker' in navigator)) return;

  navigator.serviceWorker.getRegistrations?.()
    .then((registrations) => {
      registrations.forEach((registration) => registration.unregister());
    })
    .catch(() => {
      /* ignorér – ikke kritisk */
    });

  // Tøm cachene slik at gamle /static/-chunks ikke serveres videre.
  if (typeof caches !== 'undefined') {
    caches.keys()
      .then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
      .catch(() => {
        /* ignorér */
      });
  }
}
